import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  IconButton,
  List,
  Portal,
  Text,
  ActivityIndicator,
} from 'react-native-paper'
import { View, Dimensions, Pressable } from 'react-native'
import { IconVote } from '../../../../../utils/meldd-icons'
import { useAuthContext } from '../../../../../contexts/auth-context'
import { IconClose } from '../../../../../utils/meldd-icons'
import { AppNavigation } from '../../../../../navigation/types'
import { useNavigation } from '@react-navigation/native'
import { createStyle } from '../../../../../contexts/global-styles.provider'
import {
  useArticleVotesByArticleIdQuery,
  VoteDetailFragment,
} from '../../../../../generated/graphql'
import { PaginatedFlatList } from '../../../../../@ui/containers/lists/paginated-flat.list'
import { Row } from '../../../../../@ui/containers/grid/row'
import { VoteButtonPerspective } from './vote.button'

const VOTES_PAGE_SIZE = 20

type VoteDetailsModalProps = {
  visible: boolean
  article: VoteButtonPerspective | null
  onDismiss: () => void
  onVotePress?: () => void
}

type VoteIconProps = {
  onDismiss: () => void
  onVotePress: () => void
  hasUserVote: boolean
}

const VoteIcon = ({ onDismiss, onVotePress, hasUserVote }: VoteIconProps) => {
  const styles = useStyle()
  return (
    <Pressable
      style={({ pressed }) => [styles.iconButton, pressed && styles.pressed]}
      onPress={() => {
        onDismiss()
        onVotePress()
      }}
    >
      <View style={{ left: 2 }}>
        <IconVote
          mode="text"
          size={20}
          loading={false}
          onPress={() => {
            onDismiss()
            onVotePress()
          }}
          selected={!hasUserVote}
        />
      </View>
    </Pressable>
  )
}
export function VoteDetailsModal({
  visible,
  article,
  onDismiss,
  onVotePress,
}: VoteDetailsModalProps) {
  const { t } = useTranslation('bumps')
  const navigation = useNavigation<AppNavigation>()
  const { isAnonymous } = useAuthContext()
  const articleId = article?.id ?? null
  const { data, loading, error, fetchMore, refetch } =
    useArticleVotesByArticleIdQuery({
      variables: {
        input: {
          articleId: articleId!,
          limit: VOTES_PAGE_SIZE,
          offset: 0,
        },
      },
      skip: !articleId || !visible,
    })
  const styles = useStyle()
  const screenHeight = Dimensions.get('window').height
  const maxContentHeight = screenHeight * 0.8

  const hasUserVote = article?.hasUserVote ?? false
  const voteCount = article?.voteCount ?? 0

  const handleLoadMore = useCallback(
    async (params: { offset: number; limit: number }) => {
      if (!articleId) return
      await fetchMore({
        variables: {
          input: {
            articleId,
            ...params,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return {
            ...prev,
            articleVotesByArticleId: {
              ...prev.articleVotesByArticleId,
              total: fetchMoreResult.articleVotesByArticleId.total,
              results: [
                ...prev.articleVotesByArticleId.results,
                ...fetchMoreResult.articleVotesByArticleId.results,
              ],
            },
          }
        },
      })
    },
    [articleId, fetchMore]
  )

  const handleRefresh = useCallback(async () => {
    if (!articleId) return
    await refetch({
      input: {
        articleId,
        limit: VOTES_PAGE_SIZE,
        offset: 0,
      },
    })
  }, [articleId, refetch])
  const votes =
    (data?.articleVotesByArticleId
      ?.results as unknown as VoteDetailFragment[]) || []
  return (
    <Portal>
      <Dialog style={styles.dialog} visible={visible} onDismiss={onDismiss}>
        <View style={styles.header}>
          <Row alignItems="center" style={{ flex: 1 }}>
            <Text variant={'titleMedium'} style={styles.heading}>
              {t('Current votes:')} {voteCount}
            </Text>
            {!loading &&
              !error &&
              articleId &&
              !isAnonymous &&
              onVotePress &&
              votes.length > 0 && (
                <VoteIcon
                  onDismiss={onDismiss}
                  onVotePress={onVotePress}
                  hasUserVote={hasUserVote}
                />
              )}
          </Row>
          <IconButton icon={IconClose} onPress={onDismiss} />
        </View>
        <Dialog.Content
          style={[styles.content, { maxHeight: maxContentHeight }]}
        >
          {loading && !data ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" />
            </View>
          ) : error ? (
            <View style={styles.errorContainer}>
              <Text variant="bodyMedium" style={styles.errorText}>
                {t('Error loading votes')}
              </Text>
            </View>
          ) : (
            <PaginatedFlatList
              data={votes}
              emptyMessage={t('no-votes-found')}
              emptyComponent={
                <EmptyVotesState
                  message={t('no-votes-found')}
                  hasUserVote={hasUserVote}
                  onVotePress={() => {
                    onDismiss()
                    onVotePress?.()
                  }}
                  onDismiss={onDismiss}
                />
              }
              renderItem={(info) => {
                const item = info.item as VoteDetailFragment
                const justification = item.justification?.trim() ?? ''
                const hasJustification = justification.length > 0
                return (
                  <List.Item
                    onPress={() => {
                      onDismiss()
                      navigation.navigate('Profile', { userId: item.userId })
                    }}
                    style={styles.listItem}
                    title={item.user?.username}
                    titleStyle={styles.user}
                    description={
                      hasJustification ? justification : t('No reason provided')
                    }
                    descriptionStyle={
                      !hasJustification ? styles.noJustification : undefined
                    }
                  />
                )
              }}
              total={data?.articleVotesByArticleId?.total ?? 0}
              onLoadMore={handleLoadMore}
              onRefresh={handleRefresh}
            />
          )}
        </Dialog.Content>
      </Dialog>
    </Portal>
  )
}

const useStyle = createStyle(({ theme }) => ({
  dialog: {
    backgroundColor: theme.colors.surfaceVariant,
    borderRadius: 4,
    padding: theme.spacing(0),
    minHeight: 400,
  },
  title: {},
  header: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(0),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  content: {
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  heading: {
    flex: 1,
    color: theme.colors.onSurfaceVariant,
  },
  button: {
    paddingHorizontal: theme.spacing(2),
  },
  user: {
    color: theme.colors.primary,
  },
  listItem: {
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.surfaceVariant,
    minHeight: 60,
  },
  noJustification: {
    fontStyle: 'italic',
    color: theme.colors.onSurfaceVariant,
    opacity: 0.6,
  },
  footer: {
    flexDirection: 'row',
    // margin: theme.spacing(2),
    padding: theme.spacing(4),
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  summary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    columnGap: theme.spacing(2),
  },
  loadingContainer: {
    padding: theme.spacing(4),
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
  },
  errorContainer: {
    padding: theme.spacing(4),
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
  },
  errorText: {
    color: theme.colors.error,
  },
  emptyContainer: {
    position: 'relative',
    minHeight: 200,
    padding: theme.spacing(4),
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyMessage: {
    textAlign: 'center',
    color: theme.colors.onSurfaceVariant,
    marginBottom: theme.spacing(2),
  },
  voteButtonContainer: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
  },
  voteButton: {
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.colors.primaryContainer,
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },

  iconButton: {
    padding: 8,
    borderRadius: theme.spacing(1.5),
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  pressed: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    transform: [{ scale: 0.98 }],
  },
}))

type EmptyVotesStateProps = {
  message: string
  hasUserVote: boolean
  onVotePress: () => void
  onDismiss: () => void
}

function EmptyVotesState({
  message,
  hasUserVote,
  onVotePress,
  onDismiss,
}: EmptyVotesStateProps) {
  const styles = useStyle()
  return (
    <View style={styles.emptyContainer}>
      <Text variant="titleMedium" style={styles.emptyMessage}>
        {message}
      </Text>
      <VoteIcon
        onDismiss={onDismiss}
        onVotePress={onVotePress}
        hasUserVote={hasUserVote}
      />
    </View>
  )
}
